import type { Lang } from "../i18n";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "../i18n";
import i18n from "../i18n";
import { NL_SLUGS, localizedSlug } from "./route-slugs";
import { PACKAGES, packageLang } from "./packages";

const CANONICAL_HOST = "https://www.itsolutions.be";
const SITE_NAME = "IT Solutions";
/**
 * Cache-buster voor de social-share afbeeldingen. Facebook, LinkedIn en X
 * bewaren een gescrapete preview lang; bump dit nummer telkens de beelden
 * in /public/og/ vervangen worden.
 */
const OG_V = "?v=9";
const DEFAULT_OG_IMAGE = `${CANONICAL_HOST}/og-image.jpg${OG_V}`;

/** Helper: translate a `seo` namespace key for a specific language. */
function tSeo(lang: Lang, key: string): string {
  return i18n.getFixedT(lang, "seo")(key) as unknown as string;
}

/** Content-language code per Lang (used for `inLanguage` in schema.org). */
function contentLanguage(lang: Lang): string {
  return tSeo(lang, "schema.content_language");
}

/**
 * Map van route-pad (zonder taalprefix) naar een pagina-specifieke
 * social-share afbeelding (1200×630, in /public/og/). Bestaat een pad
 * niet in de map, dan valt pageHead() terug op DEFAULT_OG_IMAGE.
 */
const PATH_TO_OG_IMAGE: Record<string, string> = {
  "/pakketten": `${CANONICAL_HOST}/og/pakketten.jpg${OG_V}`,
  "/diensten": `${CANONICAL_HOST}/og/diensten.jpg${OG_V}`,
  "/audit": `${CANONICAL_HOST}/og/audit.jpg${OG_V}`,
  "/contact": `${CANONICAL_HOST}/og/contact.jpg${OG_V}`,
  "/analyse": `${CANONICAL_HOST}/og/analyse.jpg${OG_V}`,
  "/expertise": `${CANONICAL_HOST}/og/expertise.jpg${OG_V}`,
  "/methode": `${CANONICAL_HOST}/og/methode.jpg${OG_V}`,
  "/over-ons": `${CANONICAL_HOST}/og/over-ons.jpg${OG_V}`,
  "/proces": `${CANONICAL_HOST}/og/proces.jpg${OG_V}`,
  "/technologie": `${CANONICAL_HOST}/og/technologie.jpg${OG_V}`,
  "/rapport": `${CANONICAL_HOST}/og/audit.jpg${OG_V}`,
  "/second-opinion": `${CANONICAL_HOST}/og/second-opinion.jpg${OG_V}`,
  "/blog/wat-kost-een-website-laten-maken": `${CANONICAL_HOST}/og/blog-wat-kost.jpg${OG_V}`,
};

/**
 * schema.org Service JSON-LD for the three main packages, each with a priced
 * Offer and IT Solutions as provider. Returned as a single ItemList so one
 * <script type="application/ld+json"> tag covers all three.
 */
export function packagesJsonLd(lang: Lang = DEFAULT_LANG) {
  const url = localizedUrl("/pakketten", lang);
  const providerRef = { "@id": `${CANONICAL_HOST}/#organization` };
  const image = `${CANONICAL_HOST}/og/pakketten.jpg`;
  const priceValidUntil = `${new Date().getFullYear() + 1}-12-31`;
  const areaServed = ["BE", "NL", "LU", "FR"];
  const suffix = tSeo(lang, "schema.package_suffix");
  const serviceType = tSeo(lang, "schema.package_service_type");
  const category = tSeo(lang, "schema.package_category");
  // De onafhankelijke beoordeling is een dienst, geen webpakket.
  const packages = PACKAGES.filter((p) => p.id !== "second-opinion").map((p) => ({
    name: p.name,
    description: p.features[packageLang(lang)][0],
    price: p.price.replace(/[^0-9]/g, ""),
  }));
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    inLanguage: contentLanguage(lang),
    itemListElement: packages.map((pkg, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Service",
        "@id": `${url}#${pkg.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-service`,
        name: `${pkg.name} ${suffix}`,
        serviceType,
        category,
        description: pkg.description,
        provider: providerRef,
        brand: providerRef,
        image,
        areaServed,
        inLanguage: contentLanguage(lang),
        url: `${url}#${pkg.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        offers: {
          "@type": "Offer",
          name: pkg.name,
          price: pkg.price,
          priceCurrency: "EUR",
          availability: "https://schema.org/InStock",
          businessFunction: "https://schema.org/Sell",
          priceValidUntil,
          url,
          seller: providerRef,
        },
      },
    })),
  };
}

/** Ready-to-mount head.scripts entry for the packages JSON-LD. */
export function packagesJsonLdScript(lang: Lang = DEFAULT_LANG) {
  return {
    type: "application/ld+json",
    children: JSON.stringify(packagesJsonLd(lang)),
  };
}

/**
 * FAQPage JSON-LD voor de pakketten-pagina. Beantwoordt de vragen die
 * prospects het vaakst stellen voordat ze een offerte aanvragen.
 */
export const PACKAGES_FAQ: { q: string; a: string }[] = [
  {
    q: "Wat kost een website bij IT Solutions?",
    a: "Wij werken met vaste jaarpakketten: Sport & Club à €749/jaar (gereduceerd vzw-tarief), Local Hero Basic à €990/jaar en Local Hero Premium à €1.490/jaar. Cloud & onderhoud (domeinnaam, hosting en security updates) komt bij de Local Hero-pakketten op €495/jaar en is bij Sport & Club inbegrepen. Minimale looptijd 36 maanden.",
  },
  {
    q: "Wat zit er standaard in elk pakket?",
    a: "Elk pakket bevat maatwerk-design, responsive development, EU-hosting, SSL-certificaat, technische SEO-basis, GDPR-conforme cookie-setup, en een SLA op uptime en beveiliging. Grotere pakketten voegen extra pagina's, CMS-integratie, meertaligheid en performance-optimalisatie toe.",
  },
  {
    q: "Wat is het verschil tussen eenmalig kopen en het jaarabonnement?",
    a: "Bij het eenmalige model betaal je één keer en is de site volledig jouw eigendom — inclusief broncode. Bij het jaarabonnement spreid je de investering, en zitten hosting, updates, monitoring en SEO-onderhoud continu inbegrepen zonder extra facturen.",
  },
  {
    q: "Hoe lang duurt de oplevering?",
    a: "Een Local Hero-website leveren we doorgaans in 2 tot 3 weken op. Business Architectuur duurt gemiddeld 4 tot 6 weken. Corporate Enterprise-trajecten met integraties duren 8 tot 12 weken, afhankelijk van scope en aanlevering van content.",
  },
  {
    q: "Kan ik later upgraden naar een groter pakket?",
    a: "Ja. Elk pakket is uitbreidbaar met extra pagina's, talen, integraties of een migratie naar een groter pakket. Je betaalt enkel het verschil in scope, nooit het volledige pakket opnieuw.",
  },
  {
    q: "Werken jullie ook buiten België?",
    a: "Ja. We bedienen klanten in België, Nederland, Luxemburg en Frankrijk. Alle websites zijn meertalig uit te leveren (NL/FR/EN/DE) en worden gehost op EU-servers conform GDPR.",
  },
  {
    q: "Is SEO inbegrepen in de prijs?",
    a: "Ja. Technische SEO (structuur, meta, sitemap, schema.org, snelheid, mobile-first) zit standaard in elk pakket. Voor doorlopende contentoptimalisatie en linkbuilding hebben we aparte SEO- en GEO/AI-audit-trajecten.",
  },
];

export function packagesFaqJsonLd(lang: Lang = DEFAULT_LANG) {
  const items = i18n.getFixedT(lang, "pages")("packages_faq.items", {
    returnObjects: true,
  }) as { q: string; a: string }[];
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${localizedUrl("/pakketten", lang)}#faq`,
    inLanguage: contentLanguage(lang),
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function packagesFaqJsonLdScript(lang: Lang = DEFAULT_LANG) {
  return {
    type: "application/ld+json",
    children: JSON.stringify(packagesFaqJsonLd(lang)),
  };
}

/* -------------------------------------------------------------------------- */
/*  Per-page JSON-LD helpers (localized). Shared by NL routes and $lang/*     */
/*  variants so the same schema is emitted in every language, differing only  */
/*  in `inLanguage`, `url`, `name`, `description` and breadcrumb labels.      */
/* -------------------------------------------------------------------------- */

/** `Service` schema for the /audit page in a given language. */
export function auditServiceJsonLd(lang: Lang = DEFAULT_LANG) {
  const url = localizedUrl("/audit", lang);
  const orgRef = { "@id": `${CANONICAL_HOST}/#organization` };
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name: tSeo(lang, "schema.audit_service_name"),
    serviceType: tSeo(lang, "schema.audit_service_type"),
    category: tSeo(lang, "schema.audit_category"),
    image: `${CANONICAL_HOST}/og/audit.jpg`,
    brand: orgRef,
    areaServed: ["BE", "NL", "LU", "FR"],
    audience: { "@type": "BusinessAudience", audienceType: tSeo(lang, "schema.audit_audience") },
    provider: orgRef,
    description: tSeo(lang, "audit.description"),
    inLanguage: contentLanguage(lang),
    offers: {
      "@type": "Offer",
      name: tSeo(lang, "schema.audit_service_name"),
      price: "995",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      businessFunction: "https://schema.org/Sell",
      url,
      priceValidUntil: "2026-12-31",
      seller: orgRef,
    },
    url,
  };
}

export function auditServiceJsonLdScript(lang: Lang = DEFAULT_LANG) {
  return { type: "application/ld+json", children: JSON.stringify(auditServiceJsonLd(lang)) };
}

/* -------------------------------------------------------------------------- */
/*  Homepage: Organization + WebSite + WebPage + sitelinks-navigatie          */
/* -------------------------------------------------------------------------- */

/**
 * De drie beeldverhoudingen die Google aanbeveelt voor rich results
 * (16:9, 4:3, 1:1). `scripts/generate-og.py` schrijft dezelfde bestanden weg.
 */
export function ogImageVariants(basePath: string): string[] {
  const base = basePath.replace(/\.jpg.*$/, "");
  return [`${base}.jpg${OG_V}`, `${base}-4x3.jpg${OG_V}`, `${base}-1x1.jpg${OG_V}`];
}

/** Hoofdnavigatie die als sitelinks-kandidaat wordt aangeboden (pad → nav-key). */
const SITELINK_PATHS: { path: string; key: string }[] = [
  { path: "/analyse", key: "analyse" },
  { path: "/audit", key: "footer.audit_link" },
  { path: "/diensten", key: "diensten" },
  { path: "/pakketten", key: "pakketten" },
  { path: "/proces", key: "proces" },
  { path: "/over-ons", key: "over_ons" },
  { path: "/contact", key: "contact" },
];

/**
 * Homepage-graaf: `WebPage` gekoppeld aan de sitebrede `WebSite` en
 * `Organization` uit `__root.tsx`, met een `SearchAction` (woordenlijst) en
 * een `SiteNavigationElement`-lijst zodat Google betere sitelinks kan tonen.
 */
export function homeJsonLd(lang: Lang = DEFAULT_LANG) {
  const url = localizedUrl("/", lang);
  const orgRef = { "@id": `${CANONICAL_HOST}/#organization` };
  const ts = i18n.getFixedT(lang, "seo");
  const tn = i18n.getFixedT(lang, "nav");
  const searchUrl = localizedUrl("/woordenlijst", lang);
  const images = ogImageVariants(`${CANONICAL_HOST}/og-image`);

  const navLabel = (key: string, fallback: string) => {
    const label = tn(key) as unknown as string;
    return label && label !== key ? label : fallback;
  };

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${CANONICAL_HOST}/#website`,
        url: `${CANONICAL_HOST}/`,
        name: SITE_NAME,
        alternateName: "ITsolutions",
        inLanguage: contentLanguage(lang),
        publisher: orgRef,
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${searchUrl}?q={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: ts("home.title") as unknown as string,
        description: ts("home.description") as unknown as string,
        inLanguage: contentLanguage(lang),
        isPartOf: { "@id": `${CANONICAL_HOST}/#website` },
        about: orgRef,
        publisher: orgRef,
        primaryImageOfPage: {
          "@type": "ImageObject",
          "@id": `${url}#primaryimage`,
          url: images[0],
          contentUrl: images[0],
          width: 1200,
          height: 630,
        },
        image: images,
      },
      {
        "@type": "ItemList",
        "@id": `${url}#sitenav`,
        name: SITE_NAME,
        itemListElement: SITELINK_PATHS.map(({ path, key }, i) => ({
          "@type": "SiteNavigationElement",
          position: i + 1,
          name: navLabel(key, path.replace("/", "")),
          url: localizedUrl(path, lang),
        })),
      },
    ],
  };
}

export function homeJsonLdScript(lang: Lang = DEFAULT_LANG) {
  return { type: "application/ld+json", children: JSON.stringify(homeJsonLd(lang)) };
}


/**
 * `WebPage` + `ItemList` of `SoftwareApplication` schema for the /technologie
 * page. Highlights TanStack Start, React and the rest of the stack so search
 * engines can index each technology as a distinct entity.
 */
export function technologyJsonLd(lang: Lang = DEFAULT_LANG) {
  const url = localizedUrl("/technologie", lang);
  const homeUrl = localizedUrl("/", lang);
  const orgRef = { "@id": `${CANONICAL_HOST}/#organization` };
  const t = i18n.getFixedT(lang, "home");
  const tSeoSchema = i18n.getFixedT(lang, "seo");
  const items = t("techStack.items", { returnObjects: true }) as { name: string; desc: string }[];

  const officialUrls: Record<string, string> = {
    "TanStack Start": "https://tanstack.com/start",
    "React": "https://react.dev",
    "Supabase": "https://supabase.com",
    "Tailwind CSS": "https://tailwindcss.com",
    "TypeScript": "https://www.typescriptlang.org",
    "PostgreSQL": "https://www.postgresql.org",
    "Edge Hosting": "https://www.cloudflare.com",
    "Framer Motion": "https://www.framer.com/motion",
  };

  const applicationCategories: Record<string, string> = {
    "TanStack Start": "WebApplication",
    "React": "WebApplication",
    "Supabase": "DatabaseApplication",
    "Tailwind CSS": "DeveloperApplication",
    "TypeScript": "DeveloperApplication",
    "PostgreSQL": "DatabaseApplication",
    "Edge Hosting": "WebApplication",
    "Framer Motion": "DeveloperApplication",
  };

  const techList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${url}#stack`,
    inLanguage: contentLanguage(lang),
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "SoftwareApplication",
        "@id": `${url}#${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        name: item.name,
        applicationCategory: applicationCategories[item.name] ?? "WebApplication",
        description: item.desc,
        url: officialUrls[item.name],
        provider: orgRef,
      },
    })),
  };

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    name: tSeoSchema("schema.technology_page_name"),
    url,
    inLanguage: contentLanguage(lang),
    isPartOf: { "@id": `${CANONICAL_HOST}/#website` },
    about: orgRef,
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: `${CANONICAL_HOST}/og/technologie.jpg`,
      width: 1200,
      height: 630,
    },
    mainEntity: techList,
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: tSeoSchema("schema.breadcrumb_home"),
          item: { "@type": "WebPage", "@id": homeUrl },
        },
        {
          "@type": "ListItem",
          position: 2,
          name: tSeoSchema("schema.breadcrumb_technology"),
          item: { "@type": "WebPage", "@id": url },
        },
      ],
    },
  };
}

export function technologyJsonLdScript(lang: Lang = DEFAULT_LANG) {
  return { type: "application/ld+json", children: JSON.stringify(technologyJsonLd(lang)) };
}

/** `ContactPage` schema (with BreadcrumbList) for /contact in a given language. */
export function contactPageJsonLd(lang: Lang = DEFAULT_LANG) {
  const url = localizedUrl("/contact", lang);
  const homeUrl = localizedUrl("/", lang);
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${url}#contactpage`,
    name: tSeo(lang, "schema.contactpage_name"),
    url,
    inLanguage: contentLanguage(lang),
    isPartOf: { "@id": `${CANONICAL_HOST}/#website` },
    about: { "@id": `${CANONICAL_HOST}/#organization` },
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: `${CANONICAL_HOST}/og/contact.jpg`,
      width: 1200,
      height: 630,
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: tSeo(lang, "schema.breadcrumb_home"),
          item: { "@type": "WebPage", "@id": homeUrl },
        },
        {
          "@type": "ListItem",
          position: 2,
          name: tSeo(lang, "schema.breadcrumb_contact"),
          item: { "@type": "WebPage", "@id": url },
        },
      ],
    },
  };
}

export function contactPageJsonLdScript(lang: Lang = DEFAULT_LANG) {
  return { type: "application/ld+json", children: JSON.stringify(contactPageJsonLd(lang)) };
}

/**
 * schema.org voor de rapport-leadmagneet: de pagina als WebPage, het rapport
 * zelf als gratis DigitalDocument (PDF) van IT Solutions.
 */
export function rapportJsonLd(lang: Lang = DEFAULT_LANG) {
  const url = localizedUrl("/rapport", lang);
  const t = i18n.getFixedT(lang, "rapport");
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": url,
        url,
        name: t("seo.title"),
        description: t("seo.description"),
        inLanguage: contentLanguage(lang),
        isPartOf: { "@id": `${CANONICAL_HOST}/#website` },
        publisher: { "@id": `${CANONICAL_HOST}/#organization` },
        mainEntity: { "@id": `${url}#sample-report` },
      },
      {
        "@type": "DigitalDocument",
        "@id": `${url}#sample-report`,
        name: t("schema.document_name"),
        description: t("schema.document_description"),
        fileFormat: "application/pdf",
        inLanguage: contentLanguage(lang),
        author: { "@id": `${CANONICAL_HOST}/#organization` },
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "EUR",
          availability: "https://schema.org/InStock",
          url,
        },
      },
    ],
  };
}

/** Build a fully-qualified URL for a given path (without leading language) and language. */
export function localizedUrl(path: string, lang: Lang): string {
  // `path` is always the canonical NL slug. Translate it per lang before
  // building the URL so `/en/services` and `/fr/forfaits` come out correct.
  const translated = localizedSlug(path, lang);
  const normalized = translated === "/" ? "" : translated.startsWith("/") ? translated : `/${translated}`;
  if (lang === DEFAULT_LANG) return `${CANONICAL_HOST}${normalized || "/"}`;
  return `${CANONICAL_HOST}/${lang}${normalized}`;
}

/**
 * Paden waarvoor daadwerkelijk taalvarianten bestaan als aparte route
 * (`$lang.<naam>.tsx`). Voor deze paden emitteren we hreflang-alternates
 * naar `/fr` en `/en`. Andere paden hebben alleen een NL-versie — dan
 * mag hreflang niet naar niet-bestaande URL's wijzen.
 */
const LOCALIZED_PATHS = new Set<string>(NL_SLUGS);

/**
 * `<link rel="alternate" hreflang="…">` entries for a shareable page.
 * Emits one entry per supported language plus `x-default` pointing at NL,
 * maar alleen voor paden die daadwerkelijk vertaald zijn.
 */
export function hreflangLinks(pathWithoutLang: string) {
  const base = pathWithoutLang.split("/").slice(0, 2).join("/") || "/";
  const localized =
    LOCALIZED_PATHS.has(pathWithoutLang) ||
    (pathWithoutLang.startsWith("/woordenlijst/") && LOCALIZED_PATHS.has(base));
  if (!localized) {
    return [] as Record<string, string>[];
  }
  const entries: Record<string, string>[] = SUPPORTED_LANGS.map((lang) => ({
    rel: "alternate",
    hrefLang: lang as string,
    href: localizedUrl(pathWithoutLang, lang),
  }));
  entries.push({
    rel: "alternate",
    hrefLang: "x-default",
    href: localizedUrl(pathWithoutLang, DEFAULT_LANG),
  });
  return entries;
}

/** Standard head() meta+links for a translated page. */
export function pageHead({
  lang,
  pathWithoutLang,
  title,
  description,
  ogType = "website",
  image,
  imageAlt,
  jsonLd,
}: {
  lang: Lang;
  pathWithoutLang: string;
  title: string;
  description: string;
  ogType?: string;
  image?: string;
  imageAlt?: string;
  /** Optionele schema.org JSON-LD die als <script> in de head belandt. */
  jsonLd?: unknown;
}) {
  const url = localizedUrl(pathWithoutLang, lang);
  const ogImage = image ?? PATH_TO_OG_IMAGE[pathWithoutLang] ?? DEFAULT_OG_IMAGE;
  const ogImageAlt =
    imageAlt ?? (title.includes(SITE_NAME) ? title : `${SITE_NAME} — ${title}`);
  return {
    meta: [
      { title },
      { name: "description", content: description },
      // Open Graph
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:type", content: ogType },
      { property: "og:locale", content: lang === "nl" ? "nl_BE" : lang === "fr" ? "fr_BE" : "en_US" },
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: ogImageAlt },
      // Twitter Card
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
      { name: "twitter:image:alt", content: ogImageAlt },
    ],
    links: [
      { rel: "canonical", href: url },
      ...hreflangLinks(pathWithoutLang),
    ],
    ...(jsonLd
      ? { scripts: [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }] }
      : {}),
  };
}